/**
 * Utils service
 * @file
 */
import { Injectable } from '@angular/core';
import {Observable, Subject, throwError, timer} from 'rxjs';
import {finalize, mergeMap} from "rxjs/operators";


export const genericRetryStrategy = (retryParams: {
  maxRetryAttempts: number,
  scalingDuration: number,
  retryWithStatusCodes: number[]
}) => (attempts: Observable<any>) => {
  return attempts.pipe(
    mergeMap((error, i) => {
      const retryAttempt = i + 1;
      // if maximum number of retries have been met
      // or response is a status code we don't wish to retry, throw error
      if (retryAttempt > retryParams.maxRetryAttempts ||
        (retryParams.retryWithStatusCodes.length > 0 && !retryParams.retryWithStatusCodes.includes(error.status))) {
        return throwError(error);
      }

      console.log(
        `Attempt ${retryAttempt}: retrying in ${retryAttempt *
        retryParams.scalingDuration}ms`
      );
      // retry after 1s, 2s, etc...
      return timer(retryAttempt * retryParams.scalingDuration);
    }),
    finalize(() => console.log('We are done!'))
  );
};

@Injectable({ providedIn: 'root' })
export class UtilsService {
  public contactSaveSuccess$ = new Subject();

  /**
   * Parsing currency money
   * @param currency
   */
  public parseCurrency(currency: string): string {
    return `${currency}$`;
  }

  /**
   * Date format yyyy-mm-dd
   * @param date
   */
  public dateToFormattedString(date: string): string {
    const stringToDate = new Date(date);
    const yyyy = stringToDate.getFullYear().toString();
    const mm = (stringToDate.getMonth() + 1).toString(); // getMonth() is zero-based
    const dd = stringToDate.getDate().toString();
    return yyyy + '-' + (mm[1] ? mm : '0' + mm[0]) + '-' + (dd[1] ? dd : '0' + dd[0]);
  }

  /**
   * split text with delimiter
   * @param text
   * @param maxCharacters
   */
  public splitText(text: string, maxCharacters: number = 25) {
    if (text && maxCharacters && text.length > maxCharacters) {
      return `${text.slice(0, maxCharacters)}...`;
    }
    return text;
  }

  /**
   * Currency format number
   * @param numberCurrency
   */
  public formatNumberCurrency(numberCurrency: number | string): string {
    if (!numberCurrency) {
      throw new Error('Number to format should not be null or undefined!');
    }

    if (typeof numberCurrency === 'string' && !this.isNumeric(numberCurrency)) {
      throw new Error('String should be numeric!');
    }

    const number = typeof numberCurrency === 'string' ? parseFloat(numberCurrency) : numberCurrency;
    return Math.abs(number).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
    });
  }

  /**
   * Validate if string is numeric
   * @param number
   */
  isNumeric(number: string): boolean {
    return (
      !isNaN(Number(number)) && !isNaN(parseFloat(number)) // use type coercion to parse the _entirety_ of the string (`parseFloat` alone does not do this)...
    ); // ...and ensure strings of whitespace fail
  }

  /**
   * Validate if a string is a date
   * @param date
   */
  isDate(date: string): boolean{
    return !isNaN(Date.parse(date));
  }
}
